import { useTranslation } from "react-i18next";
import { useIntl } from "react-intl";
import { Card } from "@/components/ui/card";
import { formatNumberLocale } from "@/utils/helpers";
import PendingMarginDecisions from "./Components/PendingMarginDecisions";
import { MonthlyRevenueChart, RevenueDoughnutChart, TenderStateDoughnutChart } from "./Graphs";
import { TenderRevenueStatsQuery, TenderStateStatsQuery, TenderStateStatsDocument, TenderStateStatsQueryVariables, TenderRevenueStatsDocument, TenderRevenueStatsQueryVariables } from "@/graphql/__generated__/graphql";
import { useQuery } from "@apollo/client/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import UpcomingTenderSubmissions from "./Components/UpcomingTenderSubmissions";
import { Skeleton } from "@/components/ui/skeleton";
import PendingBidDecisions from "./Components/PendingBidDecisions";
import { Button } from "@/components/ui/button";
import { isManager } from "@/utils/authentication";
import { cn } from "@/lib/utils";

const Dashboard = () => {

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["components"]);

  const { data: tenderRevenueData, loading: tenderRevenueLoading } = useQuery<TenderRevenueStatsQuery, TenderRevenueStatsQueryVariables>(TenderRevenueStatsDocument);
  const { data: tenderStatsData } = useQuery<TenderStateStatsQuery, TenderStateStatsQueryVariables>(TenderStateStatsDocument);

  // Constants
  const userTotalRevenue = tenderRevenueData?.tenderRevenueStats.userTotalRevenue ?? 0;
  const totalTendersRevenue = tenderRevenueData?.tenderRevenueStats.totalRevenue ?? 0;

  // Transform monthly revenue from array of numbers to expected format
  const monthlyRevenue = (tenderRevenueData?.tenderRevenueStats.monthlyRevenue ?? []).map((revenue, index) => {
    const now = new Date();
    const monthDate = new Date(now.getFullYear(), now.getMonth() - (11 - index), 1);
    return {
      month: monthDate.getMonth() + 1,
      year: monthDate.getFullYear(),
      revenue: revenue.toString()
    };
  });

  return (
    <div className="w-full h-[calc(100dvh-64px)] overflow-y-auto md:overflow-hidden p-4 bg-slate-100 select-none">
      {/* Responsive grid - stacks below lg breakpoint */}
      <div className="flex flex-col gap-2 h-full xl:h-full overflow-y-auto xl:overflow-hidden">
        {/* Main content wrapper - changes between flex column and grid based on breakpoint */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2 xl:h-full">
          {/* Left column - spans full width on mobile, 1 col on md, 2 cols on xl+ */}
          <div className="flex flex-col md:col-span-1 xl:col-span-2 space-y-2 h-full min-h-0">

            {/* Row 1 */}
            <div className="flex w-full gap-2">
              <Card className="px-3 pt-2 pb-1 w-full flex md:flex-row justify-between">
                {/* The Revenue over all Tenders of the organization */}
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <h1 className="text-gray-500 font-light text-sm">{t("dashboardtenderstats.tenderrevenue.title")}</h1>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 p-0 hover:cursor-help"
                        >
                          <InformationCircleIcon className="h-5 w-5" />
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80 text-sm font-normal">
                        {t("dashboardtenderstats.tenderrevenue.tooltip")}
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  {tenderRevenueLoading ? (
                    <Skeleton className="w-40 h-4 mt-2" />
                  ) : (
                    <p className="text-lg text-gray-800 font-semibold">
                      {formatNumberLocale(intl, totalTendersRevenue, "currency")}
                    </p>
                  )}
                </div>
                {/* Revenue for current user's tenders */}
                <div>
                  <div className="flex items-center justify-between gap-2">
                    <h1 className="text-gray-500 font-light text-sm">{t("dashboardtenderstats.yourrevenue.title")}</h1>
                    <HoverCard>
                      <HoverCardTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 p-0 hover:cursor-help"
                        >
                          <InformationCircleIcon className="h-5 w-5" />
                        </Button>
                      </HoverCardTrigger>
                      <HoverCardContent className="w-80 text-sm font-normal">
                        {t("dashboardtenderstats.yourrevenue.tooltip")}
                      </HoverCardContent>
                    </HoverCard>
                  </div>
                  {tenderRevenueLoading ? (
                    <Skeleton className="w-40 h-4 mt-2" />
                  ) : (
                    <p className="text-lg text-gray-800 font-semibold">
                      {formatNumberLocale(intl, userTotalRevenue, "currency")}
                    </p>
                  )}
                </div>
              </Card>
            </div>

            {/* Row 2 */}
            <div className="flex flex-col flex-1 md:min-h-[300px] xl:min-h-0">
              <Card className="flex-1 flex flex-col overflow-hidden">
                <MonthlyRevenueChart loading={tenderRevenueLoading} monthlyRevenue={monthlyRevenue} />
              </Card>
            </div>
            <div className="flex-1 md:min-h-[320px] xl:min-h-0 grid grid-cols-1 md:grid-cols-2 auto-rows-fr gap-2">
              <Card className="flex flex-col grow">
                <TenderStateDoughnutChart tenderStateStats={tenderStatsData?.tenderStateStats as any} />
              </Card>

              <Card className="flex flex-col grow">
                <RevenueDoughnutChart tenderRevenueStats={tenderRevenueData?.tenderRevenueStats as any} />
              </Card>
            </div>
          </div>

          {/* Middle column - spans full width on mobile, 1 col on md and xl+ */}
          <div className={cn("flex flex-col h-full min-h-0 md:min-h-[600px] xl:min-h-0", isManager() ? "md:col-span-1 xl:col-span-1" : "md:col-span-1 xl:col-span-2")}>
            <div className="flex-1 min-h-0">
              <UpcomingTenderSubmissions/>
            </div>
          </div>

          {/* Right column - only visible on xl+ when manager */}
          {
            isManager() && (
              <div className="hidden xl:flex xl:flex-col xl:col-span-1 space-y-2 h-full min-h-0">
                <div className="flex-1 shrink">
                  <PendingBidDecisions />
                </div>
                <div className="flex-1 shrink">
                  <PendingMarginDecisions/>
                </div>
              </div>
            )
          }
        </div>

        {/* Pending components row - visible on md to xl when manager, stacks on mobile, horizontal on md+ */}
        {
          isManager() && (
            <div className="flex flex-col md:flex-row xl:hidden gap-2 pb-10 md:pb-0">
              <div className="flex-1 min-h-[300px]">
                <PendingBidDecisions />
              </div>
              <div className="flex-1 min-h-[300px]">
                <PendingMarginDecisions/>
              </div>
            </div>
          )
        }
      </div>
    </div>
  );
};

export default Dashboard;
